import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiBookmark,
  FiExternalLink,
  FiInfo,
  FiFileText,
  FiUsers,
  FiCalendar,
  FiStar,
  FiUnlock,
  FiTrendingUp,
  FiZap,
  FiEye,
  FiBookOpen,
} from "react-icons/fi";
import {
  savePaper,
  unsavePaper,
  checkIfPaperIsSaved,
} from "../../services/paperService";
import { useUserContext } from "../../context/UserContext";
import DOMPurify from "dompurify";
import "./styles/PaperCard.css";

const PaperCard = ({ paper }) => {
  const { user, isAuthenticated } = useUserContext();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Get unique domains and fields from topicCategories
  const domains = paper.topicCategories
    ? [...new Set(paper.topicCategories.map((topic) => topic.domain))]
    : [];

  const fields = paper.topicCategories
    ? [...new Set(paper.topicCategories.map((topic) => topic.field))]
    : [];

  // Check if the paper is already saved by the user
  useEffect(() => {
    const checkSavedStatus = async () => {
      if (isAuthenticated && user && (paper._id || paper.doi)) {
        try {
          const paperId = paper._id || paper.doi;
          const saved = await checkIfPaperIsSaved(paperId);
          setIsSaved(saved);
        } catch (err) {
          // Don't display errors for background checks
        }
      }
    };

    checkSavedStatus();
  }, [isAuthenticated, user, paper._id, paper.doi]);

  // Handle save/unsave paper
  const handleSaveToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      setError("Please log in to save papers");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const paperId = paper._id || paper.doi;

      if (isSaved) {
        await unsavePaper(paperId);
        setIsSaved(false);
      } else {
        if (paperId) {
          await savePaper(paperId);
        } else {
          const paperData = {
            title: paper.title,
            abstract: paper.abstract,
            authors: paper.authors,
            doi: paper.doi,
            publicationYear: paper.publicationYear,
            citationCount: paper.citationCount,
            pdfUrl: paper.pdfUrl,
            openAccess: paper.openAccess,
            source: paper.source,
            topics: paper.topics,
            journal: paper.journal,
            publisher: paper.publisher,
          };
          await savePaper(null, paperData);
        }
        setIsSaved(true);
      }
    } catch (err) {
      setError(err.message || "Failed to update saved status");
    } finally {
      setIsLoading(false);
    }
  };

  // Safely encode URLs
  const getDetailUrl = () => {
    const id = paper.doi || paper._id;
    return id ? `/explore/${encodeURIComponent(id)}` : "/explore";
  };

  // Validate external URLs
  const validateUrl = (url) => {
    if (!url) return "";
    if (!/^https?:\/\//i.test(url)) return "";
    return url;
  };

  // Use highlights if available (from Elasticsearch)
  const title = paper._highlights?.title?.[0] || paper.title;
  const abstract =
    paper._highlights?.abstract?.[0] ||
    (paper.abstract
      ? paper.abstract.length > 150
        ? `${paper.abstract.substring(0, 150)}...`
        : paper.abstract
      : "No abstract available.");

  // Sanitize HTML content to prevent XSS attacks
  const sanitizedTitle = DOMPurify.sanitize(title);
  const sanitizedAbstract = DOMPurify.sanitize(abstract);

  // Format authors compactly
  const getAuthorsDisplay = () => {
    if (!paper.authors || paper.authors.length === 0) return "Unknown Authors";
    if (paper.authors.length === 1) return paper.authors[0];
    if (paper.authors.length <= 2) return paper.authors.join(", ");
    return `${paper.authors[0]} et al.`;
  };

  return (
    <article className="paper-card">
      {/* Header with title and badges */}
      <div className="paper-card__header">
        <Link to={getDetailUrl()} className="paper-card__title-link">
          <h3
            className="paper-card__title"
            dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
          />
        </Link>

        {/* Inline badges */}
        <div className="paper-card__badges">
          {paper.openAccess && (
            <span className="paper-card__badge paper-card__badge--open">
              <FiUnlock /> Open
            </span>
          )}
          {paper.analytics?.trendingScore > 0 && (
            <span className="paper-card__badge paper-card__badge--trending">
              <FiTrendingUp />
            </span>
          )}
          {paper.recommendationReason && (
            <span className="paper-card__badge paper-card__badge--recommended">
              <FiZap />
            </span>
          )}
          {paper._score && (
            <span className="paper-card__badge paper-card__badge--score" title="Relevance Score">
              <FiStar /> {paper._score.toFixed(1)}
            </span>
          )}
        </div>
      </div>

      {/* Meta: Authors, Year, Journal */}
      <div className="paper-card__meta">
        <span className="paper-card__authors">
          <FiUsers className="paper-card__icon" />
          {getAuthorsDisplay()}
        </span>
        <span className="paper-card__divider">•</span>
        <span className="paper-card__year">
          <FiCalendar className="paper-card__icon" />
          {paper.publicationYear || "N/A"}
        </span>
        {paper.journal && (
          <>
            <span className="paper-card__divider">•</span>
            <span className="paper-card__journal">{paper.journal}</span>
          </>
        )}
      </div>

      {/* Abstract - 2 lines */}
      <p
        className="paper-card__abstract"
        dangerouslySetInnerHTML={{ __html: sanitizedAbstract }}
      />

      {/* Categories Section - Compact */}
      {(domains.length > 0 || fields.length > 0 || (paper.topics && paper.topics.length > 0)) && (
        <div className="paper-card__categories">
          {domains.length > 0 && (
            <div className="paper-card__cat-row">
              <span className="paper-card__cat-label">Domains:</span>
              <div className="paper-card__tags">
                {domains.slice(0, 2).map((domain, index) => (
                  <span key={`domain-${index}`} className="paper-card__tag paper-card__tag--domain">
                    {domain}
                  </span>
                ))}
                {domains.length > 2 && (
                  <span className="paper-card__tag-more">+{domains.length - 2}</span>
                )}
              </div>
            </div>
          )}
          {fields.length > 0 && (
            <div className="paper-card__cat-row">
              <span className="paper-card__cat-label">Fields:</span>
              <div className="paper-card__tags">
                {fields.slice(0, 2).map((field, index) => (
                  <span key={`field-${index}`} className="paper-card__tag paper-card__tag--field">
                    {field}
                  </span>
                ))}
                {fields.length > 2 && (
                  <span className="paper-card__tag-more">+{fields.length - 2}</span>
                )}
              </div>
            </div>
          )}
          {paper.topics && paper.topics.length > 0 && (
            <div className="paper-card__cat-row">
              <span className="paper-card__cat-label">Topics:</span>
              <div className="paper-card__tags">
                {paper.topics.slice(0, 3).map((topic, index) => (
                  <span key={index} className="paper-card__tag paper-card__tag--topic">
                    {topic}
                  </span>
                ))}
                {paper.topics.length > 3 && (
                  <span className="paper-card__tag-more">+{paper.topics.length - 3}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendation Reason */}
      {paper.recommendationReason && (
        <div className="paper-card__reason">
          <FiZap className="paper-card__icon" />
          <span>{paper.recommendationReason}</span>
        </div>
      )}

      {/* Footer: Stats & Actions */}
      <div className="paper-card__footer">
        <div className="paper-card__stats">
          <span className="paper-card__stat">
            <FiBookOpen className="paper-card__icon" />
            {paper.citationCount?.toLocaleString() || 0}
          </span>
          {paper.analytics?.totalViews > 0 && (
            <span className="paper-card__stat">
              <FiEye className="paper-card__icon" />
              {paper.analytics.totalViews}
            </span>
          )}
          {paper.analytics?.totalSaves > 0 && (
            <span className="paper-card__stat">
              <FiBookmark className="paper-card__icon" />
              {paper.analytics.totalSaves}
            </span>
          )}
        </div>

        <div className="paper-card__actions">
          {paper.pdfUrl && validateUrl(paper.pdfUrl) && (
            <a
              href={validateUrl(paper.pdfUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="paper-card__btn paper-card__btn--pdf"
              onClick={(e) => e.stopPropagation()}
            >
              <FiFileText />
            </a>
          )}

          {paper.doi ? (
            <a
              href={`https://doi.org/${encodeURIComponent(paper.doi)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="paper-card__btn paper-card__btn--doi"
              onClick={(e) => e.stopPropagation()}
            >
              <FiExternalLink />
            </a>
          ) : (
            <Link
              to={getDetailUrl()}
              className="paper-card__btn paper-card__btn--details"
            >
              <FiInfo />
            </Link>
          )}

          <button
            onClick={handleSaveToggle}
            disabled={isLoading}
            className={`paper-card__btn paper-card__btn--save ${isSaved ? "saved" : ""}`}
            title={isSaved ? "Unsave Paper" : "Save Paper"}
          >
            <FiBookmark />
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && <div className="paper-card__error">{error}</div>}
    </article>
  );
};

export default PaperCard;
